import type { DashboardMetrics, WeeklyRevenuePoint } from '@leoclinic/types';
import { getSupabaseClient } from '../supabase/client';
import { reportsService } from '../reports/reports.service';

/**
 * Serviço do dashboard — agrega métricas da clínica
 */
export class DashboardService {
  /**
   * Obtém métricas do dashboard para uma clínica
   */
  async getMetrics(clinicId: string): Promise<DashboardMetrics> {
    const supabase = getSupabaseClient();
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString();
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1).toISOString();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString();

    const [appointmentsResult, revenueTodayResult, revenueMonthResult, newClientsResult, activeClientsResult] =
      await Promise.all([
        supabase
          .from('appointments')
          .select('id', { count: 'exact', head: true })
          .eq('clinic_id', clinicId)
          .gte('start_at', startOfDay)
          .lt('start_at', endOfDay)
          .neq('status', 'cancelled'),
        supabase
          .from('payments')
          .select('amount')
          .eq('clinic_id', clinicId)
          .eq('status', 'paid')
          .gte('paid_at', startOfDay)
          .lt('paid_at', endOfDay),
        supabase
          .from('payments')
          .select('amount')
          .eq('clinic_id', clinicId)
          .eq('status', 'paid')
          .gte('paid_at', startOfMonth),
        supabase
          .from('clients')
          .select('id', { count: 'exact', head: true })
          .eq('clinic_id', clinicId)
          .gte('created_at', startOfMonth),
        supabase
          .from('clients')
          .select('id', { count: 'exact', head: true })
          .eq('clinic_id', clinicId)
          .eq('status', 'active'),
      ]);

    const revenueToday = (revenueTodayResult.data ?? []).reduce(
      (sum, p) => sum + (p.amount ?? 0),
      0,
    );
    const revenueMonth = (revenueMonthResult.data ?? []).reduce(
      (sum, p) => sum + (p.amount ?? 0),
      0,
    );

    const occupancyRate = await this.computeOccupancy(clinicId, startOfDay, endOfDay);

    return {
      appointmentsToday: appointmentsResult.count ?? 0,
      revenueToday,
      revenueMonth,
      newClients: newClientsResult.count ?? 0,
      activeClients: activeClientsResult.count ?? 0,
      occupancyRate,
    };
  }

  async getWeeklyRevenue(clinicId: string): Promise<WeeklyRevenuePoint[]> {
    return reportsService.getWeeklyRevenue(clinicId);
  }

  private async computeOccupancy(clinicId: string, start: string, end: string): Promise<number> {
    const supabase = getSupabaseClient();
    const { count } = await supabase
      .from('appointments')
      .select('id', { count: 'exact', head: true })
      .eq('clinic_id', clinicId)
      .gte('start_at', start)
      .lt('start_at', end)
      .in('status', ['confirmed', 'in_progress', 'completed']);
    const booked = count ?? 0;
    const slotsPerDay = 12;
    return Math.min(100, Math.round((booked / slotsPerDay) * 100));
  }

  /**
   * Retorna métricas mock para desenvolvimento sem Supabase
   */
  getMockMetrics(): DashboardMetrics {
    return {
      appointmentsToday: 8,
      revenueToday: 420,
      revenueMonth: 12450,
      newClients: 12,
      activeClients: 156,
      occupancyRate: 72,
    };
  }
}

export const dashboardService = new DashboardService();
